/**
 * returnUrl 검증 — 클라이언트/서버 양쪽에서 안전하게 import 가능한 순수 함수.
 *
 * `state.ts`(HMAC 인코딩, Node `crypto` 의존)와 분리되어 있는 이유:
 * 이 함수는 client 번들(CapacitorBootstrap → oauth-deep-link, AuthForm →
 * native-oauth)에서도 쓰이는데, `state.ts`에 같이 두면 ES 모듈 전체 평가로
 * Node `crypto` import가 브라우저 번들로 딸려와 crypto-browserify 폴리필이
 * 강제되고 Turbopack 모듈 그래프가 깨진다. crypto 무의존 함수는 여기로 분리.
 */

/**
 * returnUrl이 외부 URL 리다이렉트(open redirect)에 악용되지 않도록 내부 경로만 허용.
 */
export function sanitizeReturnUrl(url: string | null | undefined): string {
  if (!url) return "/";
  if (typeof url !== "string") return "/";
  if (!url.startsWith("/")) return "/";
  if (url.startsWith("//")) return "/"; // protocol-relative URL 차단
  // 브라우저는 "\" 를 "/" 로 읽는다 — "/\evil.com" 은 "//evil.com"(외부 호스트)이 된다.
  if (url.startsWith("/\\")) return "/";
  // 탭·개행 같은 제어 문자는 URL 파싱 때 지워진다 — "/\t/evil.com" → "//evil.com".
  if (/[\u0000-\u001F\u007F]/.test(url)) return "/";
  return url;
}

/**
 * Where to go after login. Callers use either name: reservation pages send
 * `returnUrl`, the chat pages (where chat reminder emails land) send
 * `returnTo`. The value is decoded until stable *before* validation because
 * AuthForm decodes it again and navigates to it directly — otherwise
 * "/%2F%2Fevil.com" would pass and become "///evil.com" (an external host).
 * Backslashes are rejected too: browsers read "/\evil.com" as "//evil.com".
 */
export function resolveLoginReturnUrl(raw: string | null): string | null {
  if (!raw) return null;
  let value = raw;
  let stable = false;
  for (let i = 0; i < 4 && !stable; i++) {
    try {
      const decoded = decodeURIComponent(value);
      stable = decoded === value;
      value = decoded;
    } catch {
      return null;
    }
  }
  // Still encoded after several passes: refuse rather than let a later
  // decode turn it into something we did not validate.
  if (!stable) return null;
  const safe = sanitizeReturnUrl(value);
  if (safe === "/" || safe.includes("\\")) return null;
  return safe;
}
